<script>
  import { onMount } from 'svelte';
  
  let error = $state('');
  let isLoading = $state(false);
  let isSupported = $state(true);
  
  let challengeUrl = '/two_factor_authentication/profile/security_keys/new';
  let verificationUrl = '/two_factor_authentication/profile/security_keys';
  
  onMount(async () => {
    // Check if WebAuthn is supported in this browser
    try {
      if (!window.PublicKeyCredential) {
        isSupported = false;
        error = "Your browser doesn't support security keys. Please try using a different browser, like Chrome, Firefox, or Edge.";
      }
    } catch (e) {
      isSupported = false;
      error = "Your browser doesn't support security keys. Please try using a different browser, like Chrome, Firefox, or Edge.";
    }
  });
  
  async function registerSecurityKey() {
    try {
      error = '';
      isLoading = true;
      
      // Request the challenge from the server
      const challengeResponse = await fetch(challengeUrl, {
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (!challengeResponse.ok) {
        throw new Error('Failed to get registration challenge');
      }
      
      const publicKeyOptions = await challengeResponse.json();
      
      // Prepare the PublicKeyCredentialCreationOptions
      const publicKey = {
        challenge: base64URLToBuffer(publicKeyOptions.challenge),
        rp: publicKeyOptions.rp,
        user: {
          id: base64URLToBuffer(publicKeyOptions.user.id),
          name: publicKeyOptions.user.name,
          displayName: publicKeyOptions.user.name
        },
        pubKeyCredParams: publicKeyOptions.pubKeyCredParams,
        timeout: publicKeyOptions.timeout,
        excludeCredentials: (publicKeyOptions.excludeCredentials || []).map(credential => ({
          id: base64URLToBuffer(credential.id),
          type: credential.type,
          transports: credential.transports
        })),
        authenticatorSelection: publicKeyOptions.authenticatorSelection,
        attestation: publicKeyOptions.attestation
      };
      
      // Create the credential
      const credential = await navigator.credentials.create({ publicKey });
      
      // Format the credential for the server
      const credentialForServer = {
        id: credential.id,
        rawId: bufferToBase64URL(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bufferToBase64URL(credential.response.attestationObject),
          clientDataJSON: bufferToBase64URL(credential.response.clientDataJSON)
        }
      };
      
      // Send the credential to the server for verification
      const verificationResponse = await fetch(verificationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ credential: credentialForServer })
      });
      
      if (!verificationResponse.ok) {
        const errorData = await verificationResponse.json();
        throw new Error(errorData.error || 'Registration failed');
      }
      
      const result = await verificationResponse.json();
      if (result.location) {
        window.location.href = result.location;
      }
      
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        error = "That didn't work. Either it was cancelled or took too long. Please try again.";
      } else if (e.name === 'InvalidStateError') {
        error = "This security key might already be registered. Please try a different one.";
      } else {
        error = e.message || 'An error occurred during registration';
      }
    } finally {
      isLoading = false;
    }
  }
  
  // Utility functions for buffer/base64URL conversion
  function bufferToBase64URL(buffer) {
    const bytes = new Uint8Array(buffer);
    let str = '';
    for (const byte of bytes) {
      str += String.fromCharCode(byte);
    }
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  
  function base64URLToBuffer(base64URL) {
    const base64 = base64URL.replace(/-/g, '+').replace(/_/g, '/');
    const padLength = (4 - (base64.length % 4)) % 4;
    const padded = base64 + '='.repeat(padLength);
    const binary = atob(padded);
    const buffer = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buffer);
    
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    
    return buffer;
  }
</script>

<div class="add-security-key-container" class:is-loading={isLoading}>
  {#if error}
    <p class="error">{error}</p>
  {/if}

  <h1>Add a security key</h1>
  
  {#if isSupported}
    <p class="instruction">Have your security key ready. If it's the USB kind insert it now and then, if it has one, press the activation button when asked.</p>
    
    <button 
      type="button" 
      class="register-button" 
      onclick={registerSecurityKey} 
      disabled={isLoading}
    >
      {isLoading ? 'Processing...' : "I'm ready, let's go"}
    </button>
  {:else}
    <p class="browser-not-supported">
      Your browser doesn't support security keys. Please try using a different browser, like Chrome, Firefox, or Edge.
    </p>
  {/if}
</div>

<style>
  .add-security-key-container {
    max-width: 600px;
    margin: 0 auto;
    padding: 2rem 1rem;
  }
  
  .add-security-key-container.is-loading {
    opacity: 0.7;
    pointer-events: none;
  }
  
  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
  }
  
  .error {
    color: #b91c1c;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background-color: #fee2e2;
    border-radius: 0.375rem;
  }
  
  .instruction {
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
  
  .register-button {
    background-color: #047857;
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 1rem;
    cursor: pointer;
  }
  
  .register-button:hover:not(:disabled) {
    background-color: #065f46;
  }
  
  .register-button:disabled {
    background-color: #93c5fd;
    cursor: not-allowed;
  }
  
  .browser-not-supported {
    padding: 1rem;
    background-color: #fee2e2;
    color: #b91c1c;
    border-radius: 0.375rem;
    line-height: 1.5;
  }
</style>